import React, { useEffect, useState } from "react";
import { Col, OverlayTrigger, Placeholder, Row, Tooltip } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstConfirm from "../../components/DstConfirm/DstConfirm";
import InfoRow from "../../components/InfoRow/InfoRow";
import { useSession } from "../../contexts/SessionContext";
import { getAbsenceStatusColor, getAbsenceStatusIcon } from "../../services/MeetingAbsenceStatus";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { fetchMeetingAbsenceProof, updateAbsenceProofStatus } from "./MeetingAbsenceProof.function";

const MeetingAbsenceProof = () => {
  const { language } = useSession();
  const navigate = useNavigate();
  const { meeting_id, user_id } = useParams();
  const [absenceProof, setAbsenceProof] = useState<any>(null);
  const [loaded, setLoaded] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [nextStatus, setNextStatus] = useState<"validated" | "declined" | "present" | null>(null);

  useEffect(() => {
    fetchMeetingAbsenceProof(meeting_id!, user_id!, setAbsenceProof, setLoaded, language);
  }, [meeting_id, user_id, language]);

  if (!loaded) {
    return (
      <div className="container-fluid my-3 ">
        <div className="d-flex align-items-center mb-4 gap-2">
          <DstButton mock />
          <Placeholder as="div" animation="glow" className="mb-0" style={{ width: 200 }}>
            <Placeholder xs={8} />
          </Placeholder>
        </div>

        <Row className="s-regular">
          <Col lg={6}>
            <div className="mb-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <Placeholder animation="glow" className="mb-0" style={{ width: 80, height: 40 }}>
                  <Placeholder xs={12} />
                </Placeholder>
                <DstButton mock />
              </div>

              {[...Array(4)].map((_, index) => (
                <div key={index} className="d-flex justify-content-between border-bottom pb-2 mb-2">
                  <div className="d-flex align-items-center">
                    <Placeholder
                      as="span"
                      animation="glow"
                      className="me-2 rounded"
                      style={{ width: 200, height: 16, borderRadius: 4 }}
                    >
                      <Placeholder xs={12} />
                    </Placeholder>
                    <Placeholder as="div" animation="glow">
                      <Placeholder xs={6} />
                    </Placeholder>
                  </div>
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={4} />
                  </Placeholder>
                </div>
              ))}

              <div className="d-flex align-items-center justify-content-between mt-5 mb-3">
                <Placeholder as="h3" animation="glow" className="mb-0" style={{ width: 150, height: 40 }}>
                  <Placeholder xs={4} />
                </Placeholder>
                <DstButton mock />
              </div>

              {[...Array(3)].map((_, index) => (
                <div key={index} className="d-flex justify-content-between border-bottom pb-2 mb-2">
                  <div className="d-flex align-items-center">
                    <Placeholder as="span" animation="glow" className="me-2 rounded" style={{ width: 16, height: 16 }}>
                      <Placeholder xs={12} />
                    </Placeholder>
                    <Placeholder as="div" animation="glow">
                      <Placeholder xs={6} />
                    </Placeholder>
                  </div>
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={6} />
                  </Placeholder>
                </div>
              ))}
            </div>
          </Col>

          <Col lg={6}>
            <div>
              <Placeholder as="h3" animation="glow" className="mb-3">
                <Placeholder xs={6} />
              </Placeholder>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center">
                  <Placeholder as="strong" animation="glow" className="me-2">
                    <Placeholder xs={3} />
                  </Placeholder>
                  <div className="d-flex align-items-center">
                    <Placeholder as="span" animation="glow" className="me-2 rounded" style={{ width: 16, height: 16 }}>
                      <Placeholder xs={12} />
                    </Placeholder>
                    <Placeholder as="div" animation="glow">
                      <Placeholder xs={4} />
                    </Placeholder>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <Placeholder as="div" animation="glow" className="rounded" style={{ width: 40, height: 40 }}>
                    <Placeholder xs={12} />
                  </Placeholder>
                  <Placeholder as="div" animation="glow" className="rounded" style={{ width: 40, height: 40 }}>
                    <Placeholder xs={12} />
                  </Placeholder>
                </div>
              </div>

              <div className="my-2">
                <Placeholder as="strong" animation="glow">
                  <Placeholder xs={3} />
                </Placeholder>
                <br />
                <Placeholder as="div" animation="glow" className="mt-1">
                  <Placeholder xs={12} />
                </Placeholder>
                <Placeholder as="div" animation="glow" className="mt-1">
                  <Placeholder xs={8} />
                </Placeholder>
              </div>

              <div className="mb-3">
                <Placeholder as="strong" animation="glow">
                  <Placeholder xs={3} />
                </Placeholder>
                <br />
                <Placeholder as="div" animation="glow" className="mt-1">
                  <Placeholder xs={6} />
                </Placeholder>
              </div>

              <div className="mb-3">
                <Placeholder as="strong" animation="glow">
                  <Placeholder xs={2} />
                </Placeholder>
                <div className="mb-3 d-flex align-items-center mt-2">
                  <Placeholder as="div" animation="glow" className="me-2">
                    <Placeholder xs={6} />
                  </Placeholder>
                  <Placeholder as="span" animation="glow" className="rounded" style={{ width: 16, height: 16 }}>
                    <Placeholder xs={12} />
                  </Placeholder>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  if (!absenceProof) {
    return (
      <div className="text-center mt-5">
        <p>{translate(language, "PAGES.MEETING_ABSENCE_PROOFS.NO_PROOF_FOUND")}</p>
        <DstButton
          value={translate(language, "PAGES.MEETING_ABSENCE_PROOFS.BACK_TO_LIST")}
          clickFunction={() => navigate("/meetings/absence-proofs")}
        />
      </div>
    );
  }

  const _confirmUpdateStatus = () => {
    if (!nextStatus) return;
    updateAbsenceProofStatus(
      meeting_id!,
      user_id!,
      nextStatus,
      () => {
        fetchMeetingAbsenceProof(meeting_id!, user_id!, setAbsenceProof, setLoaded, language);
      },
      setLoaded,
      language
    );
    setShowConfirm(false);
  };

  return (
    <div className="container-fluid my-3">
      {absenceProof && (
        <Helmet>
          <title>
            Absence Proof - {absenceProof.meeting.name} - {absenceProof.user.full_name}
          </title>
        </Helmet>
      )}
      <div className="d-flex align-items-center mb-4">
        <span
          className="dst-icon-chevron-left p-2 medium-gray-background rounded me-2 clickable"
          onClick={(event: any) => completeNavigate(event, navigate, "/meetings/absence-proofs")}
        />
        <h1 className="mb-0 h3 fw-bold black-font d-flex align-items-center">
          {absenceProof.meeting.name} - {absenceProof.user.full_name}
        </h1>
      </div>
      <Row className="s-regular">
        <Col lg={6}>
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h3 className="mb-0">{translate(language, "PAGES.MEETING_ABSENCE_PROOFS.MEETING.TITLE")}</h3>
              <DstButton
                tooltipPosition="left"
                tooltip={translate(language, "PAGES.MEETING_ABSENCE_PROOFS.MEETING.VIEW_BUTTON_TOOLTIP")}
                btnImageBefore={<span className="dst-icon-link-external-01" />}
                value=""
                variant="light"
                clickFunction={() => window.open(`/meeting/${meeting_id}`, "_blank")}
              />
            </div>

            {InfoRow(
              "dst-icon-calendar-date",
              translate(language, "GLOBAL.START_DATE"),
              formatDateHumanly(language, absenceProof.meeting.dt_start, true)
            )}
            {InfoRow(
              "dst-icon-calendar-date",
              translate(language, "GLOBAL.END_DATE"),
              formatDateHumanly(language, absenceProof.meeting.dt_end, true)
            )}
            {InfoRow(
              "dst-icon-user-01",
              translate(language, "GLOBAL.ORGANIZER"),
              <span
                className="text-primary clickable"
                onClick={(event) => completeNavigate(event, navigate, `/user/${absenceProof.meeting.author.id}`)}
              >
                {absenceProof.meeting.author.full_name}
              </span>
            )}
            {InfoRow(
              "dst-icon-users-02",
              translate(language, "GLOBAL.ANIMATORS"),
              <div>
                {absenceProof.meeting.animators?.length ? (
                  absenceProof.meeting.animators.map((animator: any, index: number) => (
                    <div key={animator.id}>
                      <span
                        className="text-primary clickable"
                        onClick={(event) => completeNavigate(event, navigate, `/user/${animator.id}`)}
                      >
                        {animator.full_name}
                      </span>
                      {index < absenceProof.meeting.animators.length - 1 && ", "}
                    </div>
                  ))
                ) : (
                  <span className="text-muted">N/A</span>
                )}
              </div>
            )}

            {/* {InfoRow(
              "dst-icon-clipboard",
              translate(language, "PAGES.MEETING_ABSENCE_PROOFS.MEETING.TYPE"),
              absenceProof.meeting.meeting_type.meeting_meta_type
            )} */}

            <div className="d-flex align-items-center justify-content-between mt-5 mb-3">
              <h3 className="mb-0">{translate(language, "PAGES.MEETING_ABSENCE_PROOFS.USER.TITLE")}</h3>
              <DstButton
                value=""
                tooltip={translate(language, "PAGES.MEETING_ABSENCE_PROOFS.USER.VIEW_BUTTON_TOOLTIP")}
                tooltipPosition="left"
                btnImageBefore={<span className="dst-icon-link-external-01" />}
                variant="light"
                clickFunction={() => window.open(`/user/${user_id}`, "_blank")}
              />
            </div>
            {InfoRow(
              "dst-icon-user-circle",
              translate(language, "GLOBAL.NAME"),
              <span
                className="text-primary clickable"
                onClick={(event) => completeNavigate(event, navigate, `/user/${absenceProof.user.id}`)}
              >
                {absenceProof.user.full_name}
              </span>
            )}
            {InfoRow("dst-icon-mail-01", translate(language, "GLOBAL.EMAIL"), absenceProof.user.email)}
            {InfoRow(
              "dst-icon-award-01",
              translate(language, "GLOBAL.FLAGS"),
              <div className="d-flex align-items-center flex-wrap gap-2">
                {absenceProof.user.flags?.length > 0 ? (
                  absenceProof.user.flags.map((flag: any, index: number) => (
                    <OverlayTrigger
                      key={`flag-${index}`}
                      trigger={["hover", "focus"]}
                      placement="top"
                      overlay={<Tooltip id={`tooltip-${flag.name}`}>{flag.name}</Tooltip>}
                    >
                      <span className={`m-regular dst-icon-${flag.icon_name} blue-font`} />
                    </OverlayTrigger>
                  ))
                ) : (
                  <span className="text-muted">N/A</span>
                )}
              </div>
            )}
          </div>
        </Col>

        <Col lg={6}>
          <div>
            <h3 className="mb-3">{translate(language, "PAGES.MEETING_ABSENCE_PROOFS.TITLE")}</h3>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex">
                <strong className="me-2">{translate(language, "GLOBAL.STATUS")} :</strong>
                <div>
                  <span className={`d-flex align-items-center ${getAbsenceStatusColor(absenceProof.status)}`}>
                    {getAbsenceStatusIcon(absenceProof.status)} {absenceProof.status.toUpperCase()}
                  </span>
                </div>
              </div>
              {absenceProof.status === "pending" && (
                <div className="d-flex flex-wrap gap-2">
                  <DstButton
                    btnImageBefore={<span className="dst-icon-check" />}
                    variant="success"
                    value=""
                    tooltip={translate(language, "PAGES.MEETING_ABSENCE_PROOFS.STATUS.APPROVE")}
                    clickFunction={() => {
                      setNextStatus("validated");
                      setShowConfirm(true);
                    }}
                  />
                  <DstButton
                    btnImageBefore={<span className="dst-icon-info-circle" />}
                    variant="warning"
                    value=""
                    tooltip={translate(language, "PAGES.MEETING_ABSENCE_PROOFS.STATUS.PRESENT")}
                    clickFunction={() => {
                      setNextStatus("present");
                      setShowConfirm(true);
                    }}
                  />
                  <DstButton
                    btnImageBefore={<span className="dst-icon-x-close" />}
                    variant="danger"
                    value=""
                    tooltip={translate(language, "PAGES.MEETING_ABSENCE_PROOFS.STATUS.DECLINE")}
                    clickFunction={() => {
                      setNextStatus("declined");
                      setShowConfirm(true);
                    }}
                  />
                </div>
              )}
            </div>
            <p className="my-2 text-pre-wrap">
              <strong>{translate(language, "PAGES.MEETING_ABSENCE_PROOFS.STATUS.COMMENT")} :</strong> <br />
              {absenceProof.comment}
            </p>
            <p className="mb-3">
              <strong>{translate(language, "PAGES.MEETING_ABSENCE_PROOFS.STATUS.SENT_AT")} :</strong> <br />
              {formatDateHumanly(language, absenceProof.dt_added, true)}
            </p>
            <div className="mb-3">
              <div>
                <strong>{translate(language, "PAGES.MEETING_ABSENCE_PROOFS.STATUS.FILE")} :</strong>
              </div>
              <div className="mb-3 d-flex align-items-center">
                <div className="me-2">{absenceProof.filename}</div>
                <a
                  href={absenceProof.file_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none d-flex"
                >
                  <span className="dst-icon-link-external-02" />
                </a>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <DstConfirm
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        onConfirm={_confirmUpdateStatus}
        title={translate(language, "PAGES.MEETING_ABSENCE_PROOFS.CONFIRM.TITLE")}
        messageExplain={translate(language, "PAGES.MEETING_ABSENCE_PROOFS.CONFIRM.MESSAGE")}
        messageConfirm={
          nextStatus === "validated"
            ? translate(language, "PAGES.MEETING_ABSENCE_PROOFS.CONFIRM.APPROVE_QUESTION")
            : nextStatus === "present"
            ? translate(language, "PAGES.MEETING_ABSENCE_PROOFS.CONFIRM.PRESENT_QUESTION")
            : translate(language, "PAGES.MEETING_ABSENCE_PROOFS.CONFIRM.DECLINE_QUESTION")
        }
        confirmTextButton={
          nextStatus === "validated"
            ? translate(language, "PAGES.MEETING_ABSENCE_PROOFS.CONFIRM.BUTTON_APPROVE")
            : nextStatus === "present"
            ? translate(language, "PAGES.MEETING_ABSENCE_PROOFS.CONFIRM.BUTTON_PRESENT")
            : translate(language, "PAGES.MEETING_ABSENCE_PROOFS.CONFIRM.BUTTON_DECLINE")
        }
        buttonVariant={nextStatus === "validated" ? "success" : nextStatus === "present" ? "warning" : "danger"}
        icon={
          nextStatus === "validated"
            ? "dst-icon-check"
            : nextStatus === "present"
            ? "dst-icon-info-circle"
            : "dst-icon-x-close"
        }
        iconColor={nextStatus === "validated" ? "green" : nextStatus === "present" ? "orange" : "red"}
        iconBackgroundColor={
          nextStatus === "validated"
            ? "var(--color-light-green)"
            : nextStatus === "present"
            ? "var(--color-light-orange)"
            : "var(--color-light-pink)"
        }
      />
    </div>
  );
};

export default MeetingAbsenceProof;
